import { createApiClient, isApiFailure } from "@/lib/api/api-client";
import { logger } from "@/lib/utils/logger";

import type { PreferenceCategory, PreferencesBundle } from "./types";

export type PreferencesApiClient = ReturnType<typeof createApiClient>;

export function buildPreferencesApi(
  getToken: () => Promise<string | null>,
  getOrganizationId: () => string | undefined,
): PreferencesApiClient {
  return createApiClient({
    getToken,
    getOrganizationId,
    autoRetry429: true,
  });
}

export async function fetchPreferences(
  api: PreferencesApiClient,
): Promise<Partial<PreferencesBundle>> {
  const response = await api.request<
    | { data?: Partial<PreferencesBundle>; pagination?: unknown }
    | Partial<PreferencesBundle>
  >("/api/user-preferences", { method: "GET", timeout: 30000 });

  if (isApiFailure(response)) {
    return handlePreferenceFetchFailure(response);
  }

  const responseData = response.data;
  if (
    responseData &&
    typeof responseData === "object" &&
    !Array.isArray(responseData) &&
    "data" in responseData
  ) {
    return (responseData as { data?: Partial<PreferencesBundle> })
      .data as Partial<PreferencesBundle>;
  }

  return (responseData as Partial<PreferencesBundle>) ?? {};
}

function handlePreferenceFetchFailure(error: {
  message?: string;
  code?: string;
}): Partial<PreferencesBundle> {
  const isTimeout =
    error.message?.includes("timeout") ??
    error.message?.includes("Request timeout") ??
    error.message?.includes("aborted") ??
    error.code === "TIMEOUT";

  if (isTimeout) {
    const logLevel = process.env.NODE_ENV === "development" ? "debug" : "warn";
    logger[logLevel]("User preferences request timed out, using defaults", {
      error: error.message,
      note: "This is expected during cold starts with route compilation",
    });

    return {
      ui: {
        theme: "light",
        density: "comfortable",
        timezone: "America/New_York",
        dateFormat: "MM/DD/YYYY",
        currency: "USD",
        timeFormat: "12h",
        locale: "en",
        sidebarCollapsed: false,
        showTooltips: true,
        animationsEnabled: true,
        dashboardLayout: {},
        tablePageSize: 50,
        numberFormat: "en-US",
      },
      notifications: {
        email: { enabled: true, frequency: "immediate", types: [] },
        push: { enabled: true, types: [] },
        inApp: { enabled: true, types: [] },
        digest: { enabled: false, frequency: "daily", time: "09:00" },
      },
    };
  }

  const err = new Error(error.message ?? "Failed to fetch preferences");
  logger.error("Failed to fetch preferences", {
    error: err.message,
    code: error.code,
  });
  throw err;
}

export async function updatePreferencesRequest(
  api: PreferencesApiClient,
  payload: { category: PreferenceCategory; prefs: Record<string, unknown> },
) {
  const response = await api.put("/api/user-preferences", {
    category: payload.category,
    preferences: payload.prefs,
  });

  if (isApiFailure(response)) {
    throw new Error(response.message ?? "Failed to update preferences");
  }

  return {
    category: payload.category,
    prefs: payload.prefs,
    data: response.data,
  };
}

export async function resetPreferencesRequest(
  api: PreferencesApiClient,
  category?: PreferenceCategory,
) {
  const url = category
    ? `/api/user-preferences/reset?category=${category}`
    : "/api/user-preferences/reset";
  const response = await api.post(url, {});

  if (isApiFailure(response)) {
    throw new Error(response.message ?? "Failed to reset preferences");
  }
}

export async function exportPreferencesRequest(api: PreferencesApiClient) {
  const response = await api.get("/api/user-preferences/export");
  if (isApiFailure(response)) {
    throw new Error(response.message ?? "Failed to export preferences");
  }
  return response.data;
}

export async function importPreferencesRequest(
  api: PreferencesApiClient,
  data: string,
) {
  const response = await api.post("/api/user-preferences/import", { data });
  if (isApiFailure(response)) {
    throw new Error(response.message ?? "Failed to import preferences");
  }
}
